"use client";

import Image from "next/image";
import Link from "next/link";
import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useInViewAnimation } from "../animation/useInViewAnimation";
import { useTranslation } from "next-i18next";
import parse from "html-react-parser";

export default function Vision() {
  const ref = useRef(null);
  const inView = useInViewAnimation(ref);
  const [buttonHover, setButtonHover] = useState(false);
  const { t } = useTranslation("vision");

  const features = t("features", { returnObjects: true });

  return (
    <section
      id="design"
      ref={ref}
      className="relative w-full min-h-[80vh] md:min-h-[120vh] pt-28 font-[futura] overflow-hidden text-[#262104]"
    >
      <Image
        src="/img/vision.JPG"
        alt="Vision Visual"
        fill
        className="object-cover z-0"
        priority
      />

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="absolute top-10 left-6 md:left-10 z-20"
      >
        <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold">
          {t("headline")}
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="relative z-10 w-full h-full flex flex-col md:flex-row items-center md:items-center justify-center md:justify-end px-6 md:px-12"
      >
        <div className="bg-white/40 backdrop-blur-xs p-6 rounded-xl w-full max-w-lg lg:max-w-xl flex flex-col justify-center space-y-6 md:text-left">
          <p className="text-base md:text-base leading-relaxed">
            {parse(t("intro"))}
            <br />
            <b>
              REEFLI
              <span className="align-super text-[0.65em] font-sans ml-0.5">
                ™
              </span>
            </b>{" "}
            {t("intro2")}
          </p>

          <p className="font-semibold text-base md:text-lg">{t("subtitle")}</p>

          <ul className="space-y-4 text-sm md:text-base leading-relaxed text-left">
            {features.map((item, idx) => (
              <li key={idx}>
                <strong>{item.title}</strong>
                <span className="block mt-1">{item.text}</span>
              </li>
            ))}
          </ul>

          <div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12 inline-block"
            >
              <Link
                href="/request"
                scroll={false}
                className="relative overflow-hidden px-6 py-3 rounded-full shadow-md text-base md:text-lg font-semibold bg-[#382806] text-white group border-2 border-[#382806] inline-block"
                onMouseEnter={() => setButtonHover(true)}
                onMouseLeave={() => setButtonHover(false)}
              >
                <span
                  className={`relative z-10 transition-colors duration-300 ${
                    buttonHover ? "text-[#262104]" : "text-white"
                  }`}
                >
                  {t("cta")}
                </span>
                <span
                  className={`absolute inset-0 bg-white z-0 rounded-full transition-transform duration-500 ease-in-out ${
                    buttonHover ? "translate-y-0" : "translate-y-full"
                  }`}
                />
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
